/*
  Herfur video strip.

  The markup ships no <video> at all — each card is a lazy <img> plus a button.
  This file creates the media element on first activation and tears playback
  down when it isn't needed. That is the point: a paused <video> is not free,
  and four of them in a scrolling column cost real memory and compositing work
  on a low-end phone before anyone has pressed anything.

  What it enforces:

  - Only one clip plays at a time. Four simultaneous decodes is noise on screen
    and four simultaneous downloads on the network.
  - A clip scrolled out of view pauses itself, so a strip left behind stops
    burning battery decoding frames nobody is looking at.
  - The small rendition is used on small screens, chosen from the card's data
    attributes at build time so the large file is never fetched on a phone.

  Nothing here calls requestFullscreen, and no `controls` attribute is ever
  added — that is what keeps a clip at card size when tapped. `playsinline` is
  set both as a property and an attribute because iOS honours the attribute.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-herfur-video-wall]';
  var SMALL_SCREEN = '(max-width: 749px)';

  function pickSource(card) {
    var small = card.getAttribute('data-video-src-small');
    var full = card.getAttribute('data-video-src');
    var wantsSmall = window.matchMedia && window.matchMedia(SMALL_SCREEN).matches;
    return (wantsSmall && small) || full || small;
  }

  function buildVideo(card) {
    var src = pickSource(card);
    if (!src) return null;

    var video = document.createElement('video');
    video.className = 'herfur-videos__video';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    // iOS reads the attribute, not just the property.
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('tabindex', '-1');
    video.setAttribute('aria-hidden', 'true');
    video.preload = 'auto';
    video.src = src;

    // Behind the button, over the poster.
    card.insertBefore(video, card.querySelector('[data-herfur-video-toggle]'));
    return video;
  }

  function setup(wall) {
    if (wall.dataset.herfurVideoWallReady === 'true') return;
    wall.dataset.herfurVideoWallReady = 'true';

    var cards = Array.prototype.slice.call(wall.querySelectorAll('[data-herfur-video]'));
    if (!cards.length) return;

    function label(card, text) {
      var el = card.querySelector('[data-herfur-video-label]');
      if (el) el.textContent = text;
    }

    function pause(card) {
      var video = card.querySelector('video');
      var toggle = card.querySelector('[data-herfur-video-toggle]');
      if (video) video.pause();
      card.classList.remove('is-playing');
      if (toggle) toggle.setAttribute('aria-pressed', 'false');
      label(card, 'Play video');
    }

    /*
      Releasing a clip that is no longer in play: drop the element entirely so
      the decoder and its buffered data go with it. Without the empty-src dance
      some browsers keep the network request alive after removal.
    */
    function release(card) {
      var video = card.querySelector('video');
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
      card.classList.remove('is-playing', 'is-loading');
    }

    function play(card) {
      cards.forEach(function (other) {
        if (other !== card) {
          pause(other);
          release(other);
        }
      });

      var video = card.querySelector('video') || buildVideo(card);
      if (!video) return;

      var toggle = card.querySelector('[data-herfur-video-toggle]');
      card.classList.add('is-loading');

      var started = video.play();
      if (started && typeof started.catch === 'function') {
        started.catch(function () {
          // Decode error or a refused play — leave the poster up rather than a
          // card stuck in a playing state it never reached.
          card.classList.remove('is-loading');
          pause(card);
        });
      }

      video.addEventListener(
        'playing',
        function () {
          card.classList.remove('is-loading');
        },
        { once: true }
      );

      card.classList.add('is-playing');
      if (toggle) toggle.setAttribute('aria-pressed', 'true');
      label(card, 'Pause video');
    }

    cards.forEach(function (card) {
      var toggle = card.querySelector('[data-herfur-video-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        if (card.classList.contains('is-playing')) {
          pause(card);
        } else {
          play(card);
        }
      });
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) {
              pause(entry.target);
              release(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );
      cards.forEach(function (card) {
        observer.observe(card);
      });
    }
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: a re-rendered section arrives as fresh DOM.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
